import { Component, forwardRef, Inject, Input, OnInit } from '@angular/core';
import { BaseWidget, NgAisInstantSearch } from 'angular-instantsearch';
import { connectPagination } from 'instantsearch.js/es/connectors';
import { CatalogPage } from '@vestiaire/api-sdk-angular';
import { PlatformService } from '@app/shared/services/platform/platform.service';
import { PlatformLocation } from '@angular/common';

@Component({
  selector: 'vc-catalog-widget-pagination',
  templateUrl: './catalog-widget-pagination.component.html',
  styleUrls: ['./catalog-widget-pagination.component.scss']
})
export class CatalogWidgetPaginationComponent extends BaseWidget implements OnInit {
  @Input()
  public displayText = true;
  @Input()
  page: CatalogPage | null;
  public isCrawlable: boolean;
  public url: string;
  public state: {
    createURL: (page) => string;
    currentRefinement: number;
    nbHits: number;
    nbPages: number;
    pages: number[];
    isFirstPage: boolean;
    isLastPage: boolean;
    refine: (page) => void;
    widgetParams: any;
  };
  public SEORulesApply = true;
  private pagePadding = 5;
  private middleThresholdPage = 10;

  constructor(
    @Inject(forwardRef(() => NgAisInstantSearch)) public instantSearchParent: NgAisInstantSearch,
    private platformService: PlatformService,
    private location: PlatformLocation
  ) {
    super('CatalogWidgetPaginationComponent');
  }

  ngOnInit() {
    this.createWidget(connectPagination, {
      padding: this.pagePadding
    });
    this.initPaginationCrawlable();
    this.SEORulesApply = this.platformService.isServer;
    super.ngOnInit();
  }

  /**
   * Pagination handling in the brower history
   * - Manually push pagination state in the browser location history => [ugly workaround]
   *
   * Case 1:
   *   - If we already have the pagination state in the url "p-n",
   *     just push the same in the history by replacing the page number
   *   - (`replacePageSubstr`)
   *
   * Case 2:
   *   - If don't have the pagination state in the url
   *   - Insert the pagination state before either `queryParams` or `hashFilters` or `after currentActiveUrl`
   *   - (`insertPageSubstrBeforeChar`)
   *
   */

  refine(event: MouseEvent, page: number) {
    event.preventDefault();
    event.stopPropagation();

    if (page < 0 || page === this.state.currentRefinement || page >= this.state.nbPages) {
      return;
    }

    this.state.refine(page);

    const currentUrl = this.location.href;
    const pageNumber = page !== 0 ? `p-${page + 1}` : '';
    const pageNumberRegx = /\/p-([0-9]+\/)/g;
    const urlContainsPageNumber = currentUrl.match(pageNumberRegx);
    let urlToPush = '';

    if (urlContainsPageNumber) {
      const pageNumberMatchLength = urlContainsPageNumber[0].length;
      urlToPush = this.replacePageSubstr(currentUrl.indexOf('/p-'), pageNumberMatchLength, pageNumber, currentUrl);
    } else {
      if (currentUrl.includes('?')) {
        urlToPush = this.insertPageSubstrBeforeChar('?', `${pageNumber}/`, currentUrl);
      } else if (currentUrl.includes('#')) {
        urlToPush = this.insertPageSubstrBeforeChar('#', `${pageNumber}/`, currentUrl);
      } else {
        urlToPush = pageNumber ? `${currentUrl}${pageNumber}/` : `${currentUrl}`; // to avoid unwanted `/` to append in the end of the url
      }
    }

    this.location.pushState(null, '', urlToPush);
    window.scrollTo(0, 0);
  }

  // Insert pagination substring before char and return the new url
  insertPageSubstrBeforeChar(char: string, substring: string, url: string): string {
    const stringIndex = url.indexOf(char); // char => ? || #
    const urlPrefix = url.substring(0, stringIndex);
    const urlSuffix = url.substring(stringIndex);
    return `${urlPrefix}${substring}${urlSuffix}`;
  }

  // Replace pagination substring and return the new url
  replacePageSubstr(startIndex, endIndex, subString, url): string {
    const urlPrefix = url.substring(0, startIndex);
    const urlSuffix = url.substring(startIndex + endIndex);
    return `${urlPrefix}/${subString}/${urlSuffix}`;
  }

  initPaginationCrawlable(): void {
    if (this.page) {
      this.url = this.page.path;
      this.isCrawlable = this.page.indexation.crawlPagination;
    }
  }

  mapPages(pages: number[] = [], currentPage: number = 0) {
    return pages.filter(page => page >= currentPage && page <= currentPage + this.pagePadding);
  }

  getFinalBoundPageNumber(nbPages: number) {
    return nbPages > this.middleThresholdPage ? this.middleThresholdPage : nbPages;
  }
}
